import React, { useCallback, useEffect, useState } from "react";
import "./admin_clarity.css";
import { crmApi } from "@/services/phase1Api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import {
  Building2,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  Server,
  UserPlus,
} from "lucide-react";

export const TenantsScreen = ({ session }) => {
  const [pending, setPending] = useState([]);
  const [provisioned, setProvisioned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(null);
  const [inviting, setInviting] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        crmApi.listPendingTenants(session),
        crmApi.listProvisionedTenants(session),
      ]);
      setPending(p);
      setProvisioned(t);
    } catch {
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { load(); }, [load]);

  const handleProvision = async (prospectId) => {
    setProvisioning(prospectId);
    try {
      await crmApi.provisionTenant({ prospect_id: prospectId }, session);
      toast.success("Tenant provisioned successfully");
      load();
    } catch (e) {
      toast.error("Provisioning failed", { description: e?.response?.data?.detail || "Try again" });
    } finally {
      setProvisioning(null);
    }
  };

  const handleInvite = async (tenantId) => {
    setInviting(tenantId);
    try {
      const result = await crmApi.inviteToTenant({ tenant_id: tenantId }, session);
      await navigator.clipboard.writeText(result.invite_link);
      toast.success("Invite link copied to clipboard", { description: result.invite_link });
    } catch (e) {
      toast.error("Failed to generate invite", { description: e?.response?.data?.detail || "Try again" });
    } finally {
      setInviting(null);
    }
  };

  const handleLogin = (tenant) => {
    window.open(`${window.location.origin}/portal/${tenant.id}`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-foreground/30" />
      </div>
    );
  }

  return (
    <div className="space-y-8 admin-clarity p-6 sm:p-8 rounded-2xl" data-testid="tenants-screen">
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight">Tenant Management</h1>
        <p className="text-sm text-foreground/50 mt-1">
          Provision and manage client tenants. Customers become eligible after paying for the In-Depth Assessment.
        </p>
      </div>

      {/* Pending Tenants */}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Server className="h-4 w-4 text-[#007a9e] dark:text-amber-400" />
          Awaiting Provisioning
          {pending.length > 0 && (
            <Badge className="bg-[#007a9e]/10 dark:bg-amber-500/10 text-[#007a9e] dark:text-amber-400 border-[#007a9e]/30 dark:border-amber-500/30 text-xs ml-2">
              {pending.length}
            </Badge>
          )}
        </h2>

        {pending.length === 0 ? (
          <Card className="border border-border/60 bg-foreground/[0.02]">
            <CardContent className="p-6 text-center text-sm text-foreground/40">
              No prospects awaiting tenant provisioning. Prospects become eligible after paying for the In-Depth Assessment.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {pending.map((p) => (
              <Card key={p.id} className="border border-border/60 bg-card" data-testid={`pending-tenant-${p.id}`}>
                <CardContent className="p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#007a9e]/10 dark:bg-amber-500/10 text-[#007a9e] dark:text-amber-400">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {p.account?.name || "Unknown Company"}
                      </div>
                      <div className="text-xs text-foreground/40">
                        {p.account?.industry || "—"} &middot; {p.contact?.full_name || "—"} &middot; {p.contact?.email || "—"}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleProvision(p.id)}
                    disabled={provisioning === p.id}
                    className="h-9 rounded-lg bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90 font-medium text-sm shrink-0"
                    data-testid={`provision-btn-${p.id}`}
                  >
                    {provisioning === p.id ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Provisioning...</>
                    ) : (
                      <><Server className="h-3.5 w-3.5 mr-1.5" /> Provision</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Provisioned Tenants */}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Check className="h-4 w-4 text-emerald-400" />
          Provisioned Tenants
          {provisioned.length > 0 && (
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs ml-2">
              {provisioned.length}
            </Badge>
          )}
        </h2>

        {provisioned.length === 0 ? (
          <Card className="border border-border/60 bg-foreground/[0.02]">
            <CardContent className="p-6 text-center text-sm text-foreground/40">
              No tenants provisioned yet. Provision a tenant from the list above.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {provisioned.map((t) => (
              <Card key={t.id} className="border border-border/60 bg-card" data-testid={`provisioned-tenant-${t.id}`}>
                <CardContent className="p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">{t.name}</div>
                      <div className="text-xs text-foreground/40">
                        {t.industry || "—"} &middot; {t.contact_name || "—"} &middot; {t.contact_email || "—"}
                      </div>
                      <div className="text-[11px] text-foreground/25 mt-0.5">
                        ID: {t.id} &middot; Created {new Date(t.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInvite(t.id)}
                      disabled={inviting === t.id}
                      className="h-9 rounded-lg border-border bg-foreground/5 text-foreground hover:bg-foreground/10 text-xs"
                      data-testid={`invite-btn-${t.id}`}
                    >
                      {inviting === t.id ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Copy Invite Link
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleLogin(t)}
                      className="h-9 rounded-lg bg-foreground/10 text-foreground hover:bg-foreground/15 text-xs"
                      data-testid={`login-btn-${t.id}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      Login
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Credentials Info */}
      <Card className="border border-border/60 bg-foreground/[0.02]">
        <CardContent className="p-5">
          <div className="text-xs text-foreground/30">
            <span className="font-medium text-foreground/50">Default Admin Credentials:</span>{" "}
            Username: <code className="px-1 py-0.5 bg-foreground/5 rounded text-foreground/60">admin589A</code> &middot;
            Password: <code className="px-1 py-0.5 bg-foreground/5 rounded text-foreground/60">password589A</code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
